import { useAutoneTranslation } from '@autone/translations';
import { useContainerRef } from '@autone/utils';
import { PopoverTrigger } from '@radix-ui/react-popover';
import { CircleX } from 'lucide-react';
import {
  type ForwardedRef,
  forwardRef,
  type InputHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type RefAttributes,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { Badge } from '../Badge';
import { Skeleton } from '../Skeleton';
import {
  TailwindTooltip,
  TailwindTooltipContent,
  TailwindTooltipProvider,
  TailwindTooltipTrigger,
} from '../Tooltip';
import { cn } from '../utils';

import { MultiselectInputBox } from './components/MultiselectInputBox';
import { MultiselectInputDefaultIcons } from './components/MultiselectInputDefaultIcons';
import {
  useMultiselectContext,
  useMultiselectPopoverContext,
} from './MultiselectInput.context';
import {
  type MultiselectBaseProps,
  type MultiselectRenderPropControls,
} from './MultiselectInput.types';

interface MultiselectInputProps<T>
  extends InputHTMLAttributes<HTMLInputElement>,
    MultiselectBaseProps<T> {
  loading?: boolean;
  maxBadges?: number;
  getOptionLabel: (option: T) => string;
  renderIcons?: (controls: MultiselectRenderPropControls<T>) => ReactNode;
  renderInput?: boolean;
}

const defaultRenderIcons = <T,>(controls: MultiselectRenderPropControls<T>) => (
  <MultiselectInputDefaultIcons {...controls} />
);

const MultiselectInputComp = <T,>(
  {
    className,
    loading,
    options,
    maxBadges,
    getOptionId,
    getOptionLabel,
    renderIcons = defaultRenderIcons,
    renderInput = true,
    placeholder,
    ...props
  }: MultiselectInputProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const { common } = useAutoneTranslation();

  const popoverContext = useMultiselectPopoverContext();
  const multiselectContext = useMultiselectContext();
  const { selected, deselectOptions, disabled } = multiselectContext;

  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const input = useMemo(() => {
    const renderInputComponent =
      renderInput === true || renderInput === undefined;
    const renderDummyPlaceholder =
      renderInput === false && placeholder && selected.length === 0;

    if (renderInputComponent) {
      return (
        <input
          placeholder={placeholder ? placeholder : common('search-placeholder')}
          className={cn(
            'flex outline-none self-stretch min-w-[30%] flex-1 text-sm placeholder:text-grey-60 disabled:bg-white',
          )}
          ref={inputRef}
          role="combobox"
          type={'text'}
          disabled={disabled}
          {...props}
        />
      );
    } else if (renderDummyPlaceholder) {
      return <p className="text-grey-60">{placeholder}</p>;
    }

    return null;
  }, [renderInput, placeholder, common, disabled, props]);

  const optionsMap = useMemo(
    () =>
      Object.fromEntries(
        options.map((option) => [getOptionId(option), option]),
      ),
    [options],
  );

  const numSelected = selected.length;
  const renderAllBadges =
    maxBadges === undefined ? true : numSelected <= maxBadges;

  const badgeTooltipContainerRef = useContainerRef<HTMLDivElement>(null);

  return (
    <PopoverTrigger
      onClick={(e) => loading || (disabled && e.preventDefault())}
      asChild
    >
      <MultiselectInputBox
        ref={badgeTooltipContainerRef}
        className={cn('group', className)}
        onClick={() => inputRef.current?.focus()}
      >
        {loading ? (
          <Skeleton className="w-full self-stretch" />
        ) : (
          <>
            <div className="w-full flex flex-row flex-wrap gap-2">
              {renderAllBadges ? (
                selected.map((optionId) => (
                  <MultiselectInputSelectedOptionBadge
                    key={optionId}
                    label={
                      optionId in optionsMap
                        ? getOptionLabel(optionsMap[optionId])
                        : ''
                    }
                    onRemoveOption={() => deselectOptions([optionId])}
                  />
                ))
              ) : (
                <TailwindTooltipProvider>
                  <TailwindTooltip delayDuration={100}>
                    <TailwindTooltipTrigger asChild>
                      {/* div required to show tooltip */}
                      <div>
                        <MultiselectInputSelectedOptionBadge
                          label={`${numSelected} ${common('selected')}`}
                          onRemoveOption={() => deselectOptions(selected)}
                        />
                      </div>
                    </TailwindTooltipTrigger>
                    <TailwindTooltipContent
                      container={badgeTooltipContainerRef.current}
                      className="flex flex-wrap gap-1 p-2 bg-white max-w-[400px] max-h-[var(--radix-tooltip-content-available-height)] overflow-y-auto"
                    >
                      {selected.map((optionId) => (
                        <MultiselectInputSelectedOptionBadge
                          key={optionId}
                          label={
                            optionId in optionsMap
                              ? getOptionLabel(optionsMap[optionId])
                              : ''
                          }
                          onRemoveOption={() => deselectOptions([optionId])}
                        />
                      ))}
                    </TailwindTooltipContent>
                  </TailwindTooltip>
                </TailwindTooltipProvider>
              )}
              {input}
            </div>
            <div className="flex flex-row self-stretch items-center">
              {renderIcons?.({
                ...multiselectContext,
                ...popoverContext,
                options,
                getOptionId,
              })}
            </div>
          </>
        )}
      </MultiselectInputBox>
    </PopoverTrigger>
  );
};

const _MultiselectInput = forwardRef<
  HTMLInputElement,
  MultiselectInputProps<unknown>
>(MultiselectInputComp);

export const MultiselectInput = _MultiselectInput as <T>(
  props: MultiselectInputProps<T> & RefAttributes<HTMLInputElement>,
) => ReactNode;

interface MultiselectInputSelectedOptionBadgeProps {
  label: string;
  onRemoveOption: () => void;
}

const MultiselectInputSelectedOptionBadge = ({
  label,
  onRemoveOption,
}: MultiselectInputSelectedOptionBadgeProps) => {
  const { disabled } = useMultiselectContext();
  const handleRemoveOption = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onRemoveOption();
  };

  return (
    <Badge
      variant={disabled ? 'grey' : 'default'}
      className="flex flex-row items-center gap-1.5 p-2"
    >
      {label}
      <div
        data-testid={`badge-remove-button-${label}`}
        aria-disabled={disabled}
        onClick={!disabled ? handleRemoveOption : undefined}
        className="hover:cursor-pointer aria-disabled:cursor-default"
      >
        <CircleX size={16} />
      </div>
    </Badge>
  );
};
